import { ComponentType, lazy, LazyExoticComponent } from "react"

interface RetryOptions {
  retries?: number
  retryDelay?: number
}

/**
 * Wraps lazy import with error handling and retry logic
 */
export function safeLazy<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: RetryOptions = {}
): LazyExoticComponent<T> {
  const { retries = 3, retryDelay = 1000 } = options

  const loadWithRetry = async (attempt = 1): Promise<{ default: T } | any> => {
    try {
      return await importFn()
    } catch (error) {
      if (
        error instanceof TypeError &&
        (error.message.includes("Failed to fetch") ||
          error.message.includes("dynamically imported module") ||
          error.message.includes("Loading chunk"))
      ) {
        if (attempt <= retries) {
          console.warn(
            `Chunk loading failed, retrying (${attempt}/${retries})...`
          )
          await new Promise((resolve) => setTimeout(resolve, retryDelay))
          return loadWithRetry(attempt + 1)
        }
      }

      return { default: () => <>Error loading component</> }
    }
  }

  return lazy(() => loadWithRetry())
}
